import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmploymentTypeMasterContext
const HolidayMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useHolidayMaster = () => useContext(HolidayMasterContext);

// Provider component to wrap the app
export const HolidayMasterProvider = ({ children }) => {
  const [holidays, setHoliday] = useState([]);
  const API_URL =
    "https://hrmspayroll.prepstripe.com/api/masters/public-holidays"; // Replace with your actual API URL
  const API_URL_BULK =
    "https://hrmspayroll.prepstripe.com/api/masters/public-holidays/bulk";
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const fetchHolidays = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}?page=${page}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        // Handle nested paginated response structure
        const paginationData = response.data.data || response.data;
        const holidaysArray = Array.isArray(paginationData.data)
          ? paginationData.data
          : Array.isArray(paginationData)
            ? paginationData
            : [];

        setHoliday(holidaysArray);

        setPagination({
          currentPage: paginationData.current_page || 1,
          totalPages: paginationData.last_page || 1,
          totalItems: paginationData.total || holidaysArray.length,
          perPage: paginationData.per_page || 15,
        });
      } catch (error) {
        console.error("Error fetching holiday master:", error);
        setError(error.response?.data?.message || "Failed to fetch holidays.");
      } finally {
        setLoading(false);
      }
    },
    [API_URL],
  );

  // Fetch workplace master from the API on load
  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  // Add a new workplace
  const addHoliday = async (newWorkplace) => {
    try {
      const response = await axios.post(API_URL, newWorkplace, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setHoliday((prev) => [...prev, response.data]);
      setHoliday((prev) => [response.data.data || response.data, ...prev]);

      return { success: true };
    } catch (error) {
      console.error("Error adding holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Update an existing workplace
  const updateHoliday = async (updatedHoliday) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedHoliday.id}`,
        updatedHoliday,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const updatedData = response.data.data || response.data;
      setHoliday((prev) =>
        prev.map((holiday) =>
          holiday.id === updatedHoliday.id
            ? { ...holiday, ...updatedData }
            : holiday,
        ),
      );
      return { success: true };
    } catch (error) {
      console.error("Error updating holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to update holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Delete a employment-type
  const deleteHoliday = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setHoliday((prev) => prev.filter((workplace) => workplace.id !== id));
      return { success: true };
    } catch (error) {
      console.error("Error deleting holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to delete holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Add a new workplace
  const createBulkHoliday = async (newWorkplaces) => {
    try {
      const response = await axios.post(API_URL_BULK, newWorkplaces, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      // Ensure the response contains an array and update the state accordingly
      const responseData = response.data.data || response.data;
      if (responseData && Array.isArray(responseData)) {
        setHoliday((prev) => [...prev, ...responseData]);
        return { success: true };
      } else if (
        responseData &&
        responseData.holidays &&
        Array.isArray(responseData.holidays)
      ) {
        setHoliday((prev) => [...prev, ...responseData.holidays]);
        return { success: true };
      } else {
        console.warn("Unexpected response format:", response.data);
        // If success but no array, just refresh
        fetchHolidays();
        return { success: true };
      }
    } catch (error) {
      console.error(
        "Error creating holiday in bulk:",
        error.response?.data?.message,
      );
      setError(
        error.response?.data?.message || "Failed to create holiday in bulk.",
      );
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      holidays,
      isLoading,
      error,
      fetchHolidays,
      pagination,
      addHoliday,
      updateHoliday,
      deleteHoliday,
      createBulkHoliday,
    }),
    [holidays, isLoading, error, fetchHolidays, pagination],
  );

  return (
    <HolidayMasterContext.Provider value={value}>
      {children}
    </HolidayMasterContext.Provider>
  );
};
